import React from "react";
import { trpc } from "@/utils/trpc";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	Hash,
	Users,
	MessageSquare,
	Calendar,
	Lock,
	Globe,
} from "lucide-react";

export interface ChatOverviewProps {
	workspaceId: string;
	session?: any;
	currentWorkspace?: any;
}

export const ChatOverview: React.FC<ChatOverviewProps> = ({
	workspaceId,
	session,
	currentWorkspace,
}) => {
	const {
		isPending,
		error,
		data: channelData,
	} = useQuery(
		trpc.channel.getByWorkspace.queryOptions({
			workspaceId: Number(workspaceId),
		}),
	);

	if (isPending) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="text-center">
					<div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-primary border-b-2" />
					<p className="text-muted-foreground">Loading channels...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="rounded-lg border border-border bg-destructive/10 p-8 text-center">
					<MessageSquare className="mx-auto mb-4 h-12 w-12 text-destructive" />
					<h3 className="mb-2 font-semibold text-destructive text-lg">
						Error loading channels
					</h3>
					<p className="text-destructive/80">{error.message}</p>
				</div>
			</div>
		);
	}

	// Sample data for demonstration - you can enhance this with real channel metadata
	const enhancedChannels = channelData?.map((channel: any) => ({
		...channel,
		memberCount: Math.floor(Math.random() * 50) + 5,
		lastActivity: new Date(
			Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
		).toISOString(),
		unreadCount: Math.floor(Math.random() * 10),
		isPrivate: Math.random() > 0.7,
		description: `This is the ${channel.name} channel for collaboration and discussion.`,
	}));

	return (
		<div className="flex h-full flex-col bg-background">
			{/* Header */}
			<div className="border-border border-b bg-card px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="font-bold text-2xl text-foreground">Channels</h1>
						<p className="mt-1 text-muted-foreground text-sm">
							Browse and join channels in your workspace
						</p>
					</div>
					<button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90">
						<Hash className="h-4 w-4" />
						Create Channel
					</button>
				</div>
			</div>

			{/* Scrollable Grid Container */}
			<div className="flex-1 overflow-y-auto p-6">
				{enhancedChannels && enhancedChannels.length > 0 ? (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{enhancedChannels.map((channel: any) => (
							<Link
								key={channel.id}
								to="/$workspaceId/projects/$channelId/chats"
								params={{ workspaceId: workspaceId, channelId: channel.id }}
								className="group"
							>
								<div className="flex h-full cursor-pointer flex-col rounded-lg border border-border bg-card p-5 text-card-foreground transition-all duration-200 hover:border-accent hover:shadow-lg">
									{/* Channel Header */}
									<div className="mb-3 flex items-start justify-between">
										<div className="flex items-center gap-2">
											<div className="rounded-lg bg-accent p-2 transition-colors group-hover:bg-accent/80">
												{channel.isPrivate ? (
													<Lock className="h-5 w-5 text-accent-foreground" />
												) : (
													<Hash className="h-5 w-5 text-accent-foreground" />
												)}
											</div>
											{channel.unreadCount > 0 && (
												<span className="rounded-full bg-destructive px-2 py-1 font-semibold text-destructive-foreground text-xs">
													{channel.unreadCount}
												</span>
											)}
										</div>
									</div>

									{/* Channel Name & Description */}
									<h3 className="mb-2 font-semibold text-card-foreground transition-colors group-hover:text-primary">
										{channel.name}
									</h3>
									<p className="mb-4 line-clamp-2 flex-1 text-muted-foreground text-sm">
										{channel.description}
									</p>

									{/* Channel Stats */}
									<div className="flex items-center justify-between border-border border-t pt-3 text-muted-foreground text-xs">
										<div className="flex items-center gap-1">
											<Users className="h-3 w-3" />
											<span>{channel.memberCount} members</span>
										</div>
										<div className="flex items-center gap-1">
											<Calendar className="h-3 w-3" />
											<span>
												{new Date(channel.lastActivity).toLocaleDateString(
													undefined,
													{
														month: "short",
														day: "numeric",
													},
												)}
											</span>
										</div>
									</div>
								</div>
							</Link>
						))}
					</div>
				) : (
					<div className="flex h-full flex-col items-center justify-center">
						<div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
							<Globe className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
							<h3 className="mb-2 font-semibold text-foreground text-lg">
								No channels yet
							</h3>
							<p className="mb-4 text-muted-foreground">
								Get started by creating your first channel to collaborate with
								your team.
							</p>
							<button className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90">
								Create First Channel
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};